import {
  Button,
  Card,
  Col,
  Row,
  Space,
  Typography,
  notification,
  Input,
  Image,
  Grid,
} from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import authService from "services/auth";
import { setUserData } from "redux/slices/auth";
import { fetchRestSettings, fetchSettings } from "redux/slices/globalSettings";

const { Title, Text, Link } = Typography;
const { useBreakpoint } = Grid;

function useQueryParams() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const OtpLogin = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const query = useQueryParams();
  const screens = useBreakpoint();
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);

  const email = query.get("email") || "";
  const [tempToken, setTempToken] = useState(query.get("temp_token") || "");
  const TOTAL_SECONDS = 120;
  const getRemainingSeconds = () => {
    const sentAt = sessionStorage.getItem('otp_sent_time');
    if (!sentAt) return TOTAL_SECONDS;

    const utcString = sentAt.replace(' ', 'T') + 'Z';
    const sent = new Date(utcString).getTime();
    const now = Date.now();
    const elapsedSeconds = Math.floor((now - sent) / 1000);
    const remaining = TOTAL_SECONDS - elapsedSeconds;
    return remaining > 0 ? remaining : 0; // never go negative
  };

  const [secondsLeft, setSecondsLeft] = useState(getRemainingSeconds);

  
  function isOtpExpired(serverTimeString, minutes = 5) {
    // Ensure the server time is interpreted as UTC ISO format
    const utcString = serverTimeString.replace(' ', 'T') + 'Z';
    const sentTime = Date.parse(utcString); // this gives UTC timestamp in ms
  
    if (!Number.isFinite(sentTime)) {
      console.error("Invalid server time:", serverTimeString);
      return true;
    }
  
    const now = Date.now(); // also in UTC milliseconds
    const allowedMs = minutes * 60 * 1000;
  
    return now - sentTime > allowedMs;
  }
  

  useEffect(() => {
    if (!tempToken || !email) {
      navigate("/login", { replace: true });
    }
   
    const sentAt = sessionStorage.getItem('otp_token_payload');
    console.log({ sentAt });

    if (!sentAt || isOtpExpired(sentAt)) {
      notification.error({ message: 'OTP expired. Please login again.' });
      navigate("/login");
    }
       

  }, [tempToken, email]);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const id = setInterval(() => setSecondsLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [secondsLeft]);

  useEffect(() => {
    const id = setInterval(() => {
      const sentAt = sessionStorage.getItem('otp_token_payload');
      if (!sentAt || isOtpExpired(sentAt)) {
        notification.error({ message: 'OTP expired. Please login again.' });
        navigate('/login', { replace: true });
      }
    }, 3000);
    return () => clearInterval(id);
  }, []);

  const handleChange = (value, index) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace') {
      if (otp[index]) {
        const newOtp = [...otp];
        newOtp[index] = '';
        setOtp(newOtp);
        return;
      }
      if (index > 0) {
        const prevInput = document.getElementById(`otp-${index - 1}`);
        prevInput?.focus();
        const newOtp = [...otp];
        newOtp[index - 1] = '';
        setOtp(newOtp);
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData || window.clipboardData)
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, 6);
    if (!pasted) return;
    const newOtp = [...otp];
    for (let i = 0; i < pasted.length; i += 1) {
      newOtp[i] = pasted[i];
    }
    setOtp(newOtp);
    const nextIndex = Math.min(pasted.length, 5);
    const nextInput = document.getElementById(`otp-${nextIndex}`);
    nextInput?.focus();
  };

  const onVerify = () => {
    const joinedOtp = otp.join("");
    if (!joinedOtp) {
      notification.error({ message: "Please enter OTP" });
      return;
    }
    setLoading(true);
    authService
      .verifyDashboardOtp({ temp_token: tempToken, otp: joinedOtp })
      .then((res) => {
        const accessToken = res?.access_token;
        const user = res?.user;
        if (accessToken) localStorage.setItem("token", accessToken);
        if (user) {
          const normalizedUser = {
            ...user,
            fullName: `${user.firstname || ""} ${user.lastname || ""}`.trim(),
          };
          dispatch(setUserData(normalizedUser));
          dispatch(
            user.role === "admin" ? fetchSettings({}) : fetchRestSettings({})
          );
        }
        navigate("/dashboard", { replace: true });
      })
      .finally(() => setLoading(false));
  };

  const onResend = () => {
    try {
      const raw = sessionStorage.getItem('otp_login_payload');
      const saved = raw ? JSON.parse(raw) : null;
      if (!saved?.identifier || !saved?.password) {
        navigate('/login');
        return;
      }
      setLoading(true);
      const body = {
        temp_token: tempToken,
        password: saved.password,
      };
      // Determine identifier type
      if (saved.identifier.includes('@')) {
        body.email = saved.identifier;
      } else {
        body.phone = saved.identifier.replace(/[^0-9]/g, '');
      }
      authService
        .login(body)
        .then((res) => {
          // Expect backend to send a fresh temp_token and message
          const data = res?.data || res;
          const newTemp = data?.temp_token || tempToken;
          if (data?.message) notification.info({ message: data.message });
          // reset timer and update temp token in URL without full reload
          setSecondsLeft(120);
          const params = new URLSearchParams(window.location.search);
          params.set('temp_token', newTemp);
          window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
          setTempToken(newTemp);
          if (data?.time) {
            try { sessionStorage.setItem('otp_sent_time', data.time); } catch {}
          } else {
            try { sessionStorage.setItem('otp_sent_time', new Date().toISOString()); } catch {}
          }
        })
        .finally(() => setLoading(false));
    } catch {
      navigate('/login');
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: screens && screens.md ? "1.5rem" : "0.75rem",
        backgroundColor: "#f9fafb",
      }}
    >
      <Row
        gutter={[32, 32]}
        align="middle"
        justify="center"
        style={{
          width: "100%",
          maxWidth: 1100,
          background: "#fff",
          padding: screens && screens.md ? "1.5rem" : "0.75rem",
          borderRadius: "16px",
        }}
      >
        {/* Left Illustration */}
        {screens && screens.md && (
          <Col xs={24} md={12}>
            <Image src='/tina_logo_2.png' width={200} 
            preview={false}
            background="red"
            />

            <img
              src="/login-img.svg"
              alt="Verification Illustration"
              style={{ width: "100%", height: "auto" }}
            />
          </Col>
        )}

        {/* Right Section */}
        <Col xs={24} md={12}>
          <Card
            bordered={false}
            style={{
              boxShadow: "none",
              textAlign: "center",
              background: "transparent",
            }}
          >
            <Space direction="vertical" size={screens && screens.md ? "large" : "middle"} style={{ width: "100%" }}>
              <div>
                <Title level={3} style={{ marginBottom: 0 }}>
                  Enter your passcode
                </Title>
                <Text type="secondary">
                  We sent a code to <strong>{email}</strong>. Enter below to get started.
                </Text>
              </div>

              {/* OTP Inputs */}
              <div
                onPaste={handlePaste}
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: screens && screens.md ? 16 : 12,
                  marginBottom: screens && screens.md ? 16 : 12,
                  columnGap: screens && screens.md ? 8 : 6,
                }}
              >
                {otp.map((num, idx) => (
                  <Input
                    key={idx}
                    id={`otp-${idx}`}
                    value={num}
                    onChange={(e) => handleChange(e.target.value, idx)}
                    onKeyDown={(e) => handleKeyDown(e, idx)}
                    onPaste={handlePaste}
                    onFocus={(e) => e.target.select()}
                    maxLength={1}
                    size="large"
                    autoFocus={idx === 0}
                    style={{
                      width: screens && screens.md ? "48px" : "40px",
                      height: screens && screens.md ? "48px" : "40px",
                      textAlign: "center",
                      fontSize: screens && screens.md ? "20px" : "18px",
                      fontWeight: 600,
                      borderRadius: 8,
                      borderWidth: "2px",
                      borderColor: "rgba(246, 166, 9, 0.9)",
                    }}
                  />
                ))}
              </div>

              {/* Timer */}
              <Text>
                Code expires in :{" "}
                <Text strong>
                  {String(Math.floor(secondsLeft / 60)).padStart(2, '0')}
                  :
                  {String(secondsLeft % 60).padStart(2, '0')}
                </Text>
              </Text>

              <Button
                type="primary"
                block
                size="large"
                loading={loading}
                onClick={onVerify}
                style={{
                  borderRadius: 8,
                  background: "rgba(246, 166, 9, 0.9)",
                  border: "none",
                  height: 48,
                  fontSize: 16,
                  fontWeight: 600,
                  color: "#fff",
                }}
              >
                Send Verification
              </Button>

              <Text>
                Didn’t receive code?{" "}
                <Link 
                onClick={onResend} disabled={loading || secondsLeft != 0}>
                  Resend Code
                </Link>
              </Text>
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default OtpLogin;
